import * as React from 'react';
import { Box, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUploadContext from '../../../../../../../hooks/useUploadContext/useUploadContext';
import { MediaTypes } from '../../../../../../../commands/practitioner/enums/MediaTypes';
import { FileUploadStyle } from './styles';
import ButtonUI from '../../../../../../UI/Button/Button';
import heic2any from 'heic2any';
import useAppContext from '../../../../../../../hooks/useAppContext/useAppContext';
import { DropZoneUI } from '../../../../../../UI/DropZone/DropZoneUI';
import {
  detectMediaTypeFromFile,
  isHeicLikeFile,
  isWavOggFile,
  isGifFile,
} from '../../../../../../../utils/mediaDetection';
import useCurrentSessionState from '../../../../../../../hooks/useCurrentSessionState/useCurrentSessionState';
import { isVersionLessThan, commandVersion1_5_0 } from '../../../../../../../utils/versionCheck';

interface FileUploadProps {
  onFileUpload: Function;
  onTypeSelect: Function;
  index: number;
  value: number;
}

const FileUpload = (props: FileUploadProps) => {
  const { t } = useTranslation();
  const { onFileUpload, onTypeSelect, index, value } = props;
  const { hasDevice } = useAppContext();
  const currentState = useCurrentSessionState() || {};
  const deviceVersion = typeof currentState?.version === 'string' ? currentState.version : '0.0.0';
  const { uploadedFileName, setUploadedFileName } = useUploadContext();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [tempFileName, setTempFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { fileUploadContainer } = FileUploadStyle;
  const maximumFileName = 25;

  const getUploadExtensionKey = () => {
    if (!hasDevice) return 'upload-extension';
    if (isVersionLessThan(deviceVersion, commandVersion1_5_0)) return 'upload-extension-plus';
    return 'upload-extension-plus_1.5.0';
  };

  const trimFileName = (fileName: string) => {
    if (fileName.length <= maximumFileName) {
      return fileName;
    }

    const extensionIndex = fileName.lastIndexOf('.');

    if (extensionIndex <= 0) {
      return fileName.slice(0, maximumFileName);
    }

    const extension = fileName.slice(extensionIndex);
    const maxBaseLength = maximumFileName - extension.length;

    if (maxBaseLength <= 0) {
      return fileName.slice(0, maximumFileName);
    }

    return `${fileName.slice(0, maxBaseLength)}${extension}`;
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleSnackbarOpen = () => {
    setOpenSnackbar(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogOpen = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setOpenDialog(true);
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    setTempFileName(inputValue);
    const intervalId = setInterval(() => {
      setUploadedFileName(inputValue);
    }, 500);

    setTimeout(() => {
      clearInterval(intervalId);
    }, 500);
  };

  const handleFileNameConfirm = () => {
    if (selectedFile) {
      setUploadedFileName(tempFileName);
      onFileUpload(selectedFile);
      setOpenDialog(false);
    }
  };

  const fileNameLogic = useCallback(
    (file: File) => {
      const defaultFileName = file.name;
      const imageSize = file.size;
      const maximumFileSize = 32 * 1024 * 1024; // 32MB;

      if (imageSize > maximumFileSize) {
        setMessage(t('too-big-size'));
        handleSnackbarOpen();
        return;
      }

      setSelectedFile(file);
      setTempFileName(defaultFileName);
      setMessage('');
      handleDialogOpen();
    },
    [t]
  );

  const handleChange = useCallback(
    async (selectedFiles: File[] | []) => {
      if (selectedFiles.length === 0) return;
      if (selectedFiles.length > 1) {
        setMessage(t('to-much-uploaded-files'));
        handleSnackbarOpen();
        return;
      }

      let file = selectedFiles[0];
      const trimmedFileName = trimFileName(file.name);

      if (trimmedFileName !== file.name) {
        file = new File([file], trimmedFileName, { type: file.type });
      }

      let newType: MediaTypes | null = null;
      let sendingFile = file;

      if (isHeicLikeFile(file)) {
        const blob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8,
        });

        if (blob instanceof Blob) {
          sendingFile = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
            type: 'image/jpeg',
          });
          newType = MediaTypes.IMAGE;
        }
      } else {
        newType = detectMediaTypeFromFile(file);
      }

      if (newType === null) {
        setMessage(t(getUploadExtensionKey()));
        handleSnackbarOpen();
        return;
      }

      if (hasDevice && isWavOggFile(file) && isVersionLessThan(deviceVersion, commandVersion1_5_0)) {
        setMessage(t(getUploadExtensionKey()));
        handleSnackbarOpen();
        return;
      }

      if (hasDevice && isGifFile(file)) {
        setMessage(t(getUploadExtensionKey()));
        handleSnackbarOpen();
        return;
      }

      onTypeSelect(null, newType);
      fileNameLogic(sendingFile);
    },
    [fileNameLogic, onTypeSelect, t, hasDevice, deviceVersion]
  );

  return (
    <>
      {index === value && (
        <Box sx={fileUploadContainer}>
          <DropZoneUI onDrop={handleChange} desc={t('drop-zone')} />
          <Snackbar className="snack-bar" open={openSnackbar} autoHideDuration={4000} onClose={handleSnackbarClose}>
            <Alert className="alert" elevation={6} variant="filled" severity="info">
              {message}
            </Alert>
          </Snackbar>
          <Dialog className="dialog" open={openDialog} onClose={handleDialogClose} sx={FileUploadStyle.dialog}>
            <DialogTitle>{t('confirm-file-name')}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                type="text"
                fullWidth
                value={tempFileName}
                onChange={handleFileNameChange}
                className={!!uploadedFileName ? '' : 'empty'}
                inputProps={{ maxLength: maximumFileName }}
              />
            </DialogContent>
            <DialogActions>
              <ButtonUI
                className={'cancel-btn'}
                onClickHandler={handleDialogClose}
                text={t('cancel')}
                variant={'outlined'}
              />
              <ButtonUI
                className={'confirm-btn'}
                onClickHandler={handleFileNameConfirm}
                text={t('confirm')}
                variant="contained"
              />
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default FileUpload;
